/**
 * leadFieldOptions.js
 * ───────────────────
 * Single source of truth for the option lists rendered on the Lead Focus
 * "Information" tab (`LeadContactSections.jsx`) AND the manual multi-check
 * filter UI on the Leads → Segments "New segment" lightbox
 * (`LeadSegments.jsx`).
 *
 * Iter 139.3 (2026-05-26 user request): the segment builder's manual
 * filter tab used to expose only ~10 hand-picked structured filters
 * (specialty / state / ai_score / etc). Reps asked for the FULL field
 * universe that exists on the Lead Focus page so they could build a
 * segment off any value they enter into the lead profile. Owning the
 * option lists here lets both surfaces stay in sync forever.
 *
 * The shape is deliberately flat (string id → array of options) so
 * upstream callers can render multi-check pills, single-select
 * dropdowns, or autocomplete inputs interchangeably.
 */

import { useEffect, useSyncExternalStore } from "react";
import DEFAULT_OPTIONS from "./leadFieldOptionDefaults.json";

// Bundled defaults; replaced in place by admin-configured values from
// /api/lead-field-options so module-level consumers (filter groups, CSV map)
// keep the same array references.
export const OPT = Object.fromEntries(Object.entries(DEFAULT_OPTIONS).map(([k, v]) => [k, [...v]]));

const FIELD_OPTIONS_TTL_MS = 5 * 60 * 1000;

// Option store bound to one endpoint; arrays are mutated in place so module-level references stay valid.
function createOptionStore(opt, path) {
  let version = 0;
  let loadedAt = 0;
  let inflight = null;
  const listeners = new Set();
  const apply = (map) => {
    if (!map || typeof map !== "object") return;
    Object.entries(map).forEach(([k, vals]) => {
      if (!Array.isArray(vals)) return;
      if (!opt[k]) opt[k] = [];
      opt[k].splice(0, opt[k].length, ...vals);
    });
    version += 1;
    listeners.forEach((fn) => fn());
  };
  const load = ({ force = false } = {}) => {
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
    if (!token) return Promise.resolve(opt);
    if (inflight) return inflight;
    if (!force && loadedAt && Date.now() - loadedAt < FIELD_OPTIONS_TTL_MS) return Promise.resolve(opt);
    inflight = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.options) { apply(data.options); loadedAt = Date.now(); }
        return opt;
      })
      .catch(() => opt)
      .finally(() => { inflight = null; });
    return inflight;
  };
  const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
  const getVersion = () => version;
  const useOptions = () => {
    useSyncExternalStore(subscribe, getVersion);
    useEffect(() => { load(); }, []);
    return opt;
  };
  return { apply, load, useOptions };
}

const _leadStore = createOptionStore(OPT, "lead-field-options");
export const applyFieldOptions = _leadStore.apply;
export const loadFieldOptions = _leadStore.load;
/** Subscribes the caller to option updates and refreshes stale options on mount. */
export const useFieldOptions = _leadStore.useOptions;

// EHI Client lists — stored separately from Lead lists; bundled Lead defaults are the fallback.
export const EHI_OPT = Object.fromEntries(Object.entries(DEFAULT_OPTIONS).map(([k, v]) => [k, [...v]]));
const _ehiStore = createOptionStore(EHI_OPT, "ehi-client-field-options");
export const applyEhiFieldOptions = _ehiStore.apply;
export const useEhiFieldOptions = _ehiStore.useOptions;

// ── Derived value helpers ───────────────────────────────────────────────
// Iter 139.5 (ioannis 2026-05-26 — "Internal Medicine + MD 1 returns 0
// when every lead is by definition ≥1 MD"): the rep-editable
// `providers_multi` field on Lead Focus is sparsely tagged across the
// real lead pool, so a pure read of `lead.providers_multi` zeroes-out
// most filters. These derivers fall back to NPPES / practice
// `group_size` / contacts-roster shape so the multi-check section
// matches what's actually true about each lead.
function _toInt(x) { const n = Number(x); return Number.isFinite(n) ? n : null; }
function _groupSize(lead) {
  return (
    _toInt(lead?.group_size) ||
    _toInt(lead?.practice?.group_size) ||
    _toInt(lead?.nppes?.providers_count) ||
    (Array.isArray(lead?.contacts) && lead.contacts.length) ||
    (Array.isArray(lead?.nppes?.providers) && lead.nppes.providers.length) ||
    null
  );
}
export function deriveProviderBuckets(lead) {
  const n = _groupSize(lead);
  // Every lead is by definition a healthcare provider with ≥1 MD/NP/PA,
  // so when we have NO group-size data we still claim "MD 1" so the
  // single-doctor segment doesn't lose every untagged lead.
  if (!n) return ["MD 1", "Solo Practice"];
  if (n === 1) return ["MD 1", "Solo Practice"];
  if (n === 2) return ["MD 2"];
  if (n === 3) return ["MD 3"];
  if (n === 4) return ["MD 4"];
  if (n === 5) return ["MD 5"];
  if (n <= 10) return ["MD 6 to 10"];
  if (n <= 20) return ["MD 11 to 20"];
  return ["MD 20+"];
}
function _coerceArr(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === "") return [];
  return [v];
}
function _deriveSpecialtyMulti(lead) {
  // Read every place a specialty may hide so the multi-check filter
  // catches leads that haven't been hand-tagged.
  const out = new Set();
  _coerceArr(lead?.specialty_multi).forEach((s) => out.add(s));
  if (lead?.specialty) out.add(lead.specialty);
  _coerceArr(lead?.contacts).forEach((c) => { if (c?.specialty) out.add(c.specialty); });
  _coerceArr(lead?.nppes?.taxonomies).forEach((t) => { if (t?.classification) out.add(t.classification); });
  return Array.from(out);
}
function _triBool(v) {
  if (v === true) return ["Yes"];
  if (v === false) return ["No"];
  return ["Unknown"];
}
function _amount(v) {
  const n = _toInt(v);
  if (n == null) return null;
  if (n < 100_000) return "Under $100K";
  if (n < 250_000) return "$100K – $250K";
  if (n < 500_000) return "$250K – $500K";
  if (n < 1_000_000) return "$500K – $1M";
  if (n < 5_000_000) return "$1M – $5M";
  return "$5M+";
}
const TRI_STATE_OPTIONS = ["Yes", "No", "Unknown"];
const FIN_BUCKETS = ["Under $100K", "$100K – $250K", "$250K – $500K", "$500K – $1M", "$1M – $5M", "$5M+"];

/**
 * EXTENDED_FILTER_GROUPS
 * ──────────────────────
 * Field-by-field structure mirroring the Lead Focus "Information" tab
 * sections, used by the LeadSegments "New segment" → Manual filters
 * "All Lead Focus fields" multi-check section. Each entry's `key`
 * MUST match the actual MongoDB lead field name so server-saved values
 * intersect correctly.
 *
 * `multi: true` → the lead document stores an array (intersect match).
 * `multi: false` → the lead document stores a single scalar string
 *                  (set-membership of c.value).
 * `valueGetter(lead)` → optional fallback projector when the stored
 *                  field is empty. Used to derive provider counts /
 *                  CMS booleans / financial buckets from real source
 *                  shape (NPPES, group_size, signals, medicare, …).
 */
export const EXTENDED_FILTER_GROUPS = [
  {
    id: "identity",
    label: "Contact & Practice Identity",
    fields: [
      { key: "organizational_role", label: "Organizational Role", options: OPT.organizationalRole, multi: true },
      { key: "practice_type",       label: "Practice Type",       options: OPT.practiceType,       multi: false },
      { key: "specialty_multi",     label: "Specialty",           options: OPT.specialty,          multi: true, valueGetter: _deriveSpecialtyMulti },
      { key: "time_zone",           label: "Time Zone",           options: OPT.timeZone,           multi: false },
      { key: "providers_multi",     label: "Number of Providers", options: OPT.providers,          multi: true, valueGetter: deriveProviderBuckets },
      { key: "practice_characteristics", label: "Practice Characteristics", options: OPT.practiceCharacteristics, multi: false },
    ],
  },
  {
    id: "pipeline",
    label: "Pipeline & Lead Status",
    fields: [
      { key: "lead_stage",      label: "Lead Stage",     options: OPT.leadStage,      multi: false },
      { key: "lead_source",     label: "Lead Source",    options: OPT.leadSource,     multi: false },
      { key: "lead_rating",     label: "Lead Rating",    options: OPT.leadRating,     multi: false },
      { key: "lead_status",     label: "Lead Status",    options: OPT.leadStatus,     multi: false },
      { key: "lead_tag",        label: "Lead Tag",       options: OPT.leadTag,        multi: true  },
      { key: "lead_stage_stands", label: "Lead Stage Stands", options: OPT.stageStands, multi: false },
      { key: "last_call_status", label: "Last Call Communication Status", options: OPT.lastCallStatus, multi: false },
    ],
  },
  {
    id: "products",
    label: "Products & Qualification",
    fields: [
      { key: "product_type",          label: "Product Type",          options: OPT.productType,    multi: true  },
      { key: "feature_interest",      label: "Feature Interest",      options: OPT.featureInterest, multi: true },
      { key: "product_value_drivers", label: "Product Value Drivers", options: OPT.productValue,   multi: true  },
      { key: "key_value_proposition", label: "Key Value Proposition", options: OPT.keyValueProp,   multi: true  },
      { key: "integration_requirements", label: "Integration Requirements", options: OPT.integrationReq, multi: true },
      { key: "migration_needs",       label: "Migration Needs",       options: OPT.migrationNeeds, multi: false },
      { key: "implementation_timeline", label: "Implementation Timeline", options: OPT.implTimeline, multi: false },
    ],
  },
  {
    id: "budget",
    label: "Budget & Deal Details",
    fields: [
      { key: "budget_range",     label: "Budget Range",     options: OPT.budgetRange,    multi: false },
      { key: "upfront_budget",   label: "Upfront Budget",   options: OPT.upfrontBudget,  multi: false },
      { key: "pricing_fit",      label: "Pricing Fit",      options: OPT.pricingFit,     multi: false },
      { key: "prospect_decision_timeline", label: "Prospect Decision Timeline", options: OPT.prospectTimeline, multi: false },
    ],
  },
  {
    id: "sentiment",
    label: "Engagement & Sentiment Intelligence",
    fields: [
      { key: "interest_level",        label: "Lead Interest Level",    options: OPT.interestLevel,    multi: false },
      { key: "internal_rating",       label: "Internal Lead Rating",   options: OPT.internalRating,    multi: false },
      { key: "engagement_level",      label: "Engagement Level",       options: OPT.engagementLevel,  multi: false },
      { key: "overall_sentiment",     label: "Overall Sentiment",      options: OPT.sentiment,        multi: false },
      { key: "reason_category",       label: "Reason Category",        options: OPT.reasonCategory,   multi: true  },
      { key: "pain_point_category",   label: "Pain Point Category",    options: OPT.painCategory,     multi: true  },
      { key: "question_insight_group", label: "Question Insight Group", options: OPT.questionInsight, multi: true },
      { key: "marketing_segments",    label: "Marketing Segments",     options: OPT.marketingSeg,     multi: true  },
    ],
  },
  {
    id: "demo",
    label: "Demo & Appointment Tracking",
    fields: [
      { key: "demo_rescheduled",     label: "Demo Rescheduled",       options: OPT.demoReschedule,    multi: false },
      { key: "followups_scheduled",  label: "# Follow-Ups Scheduled", options: OPT.followUpCount,     multi: false },
      { key: "next_action_post_demo", label: "Next Action Post-Demo", options: OPT.nextActionPostDemo, multi: false },
    ],
  },
  {
    id: "location",
    label: "Practice Location",
    fields: [
      { key: "city",  label: "City",  options: [], multi: true, forceDerive: true, freeText: true, valueGetter: (l) => {
        const c = l?.city || l?.address?.city || l?.nppes?.practice_address?.city;
        return c ? [c] : [];
      } },
      { key: "state", label: "State", options: ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR"], multi: true, forceDerive: true, valueGetter: (l) => {
        const s = l?.state || l?.address?.state || l?.nppes?.practice_address?.state;
        return s ? [String(s).toUpperCase()] : [];
      } },
      { key: "medicare_beneficiaries_bucket", label: "Medicare Beneficiary Count", options: ["<50","50–200","200–500","500–1000","1000+"], multi: true, forceDerive: true, valueGetter: (l) => {
        const n = Number(l?.medicare?.beneficiaries ?? l?.medicare_benes ?? l?.beneficiaries);
        if (!Number.isFinite(n) || n <= 0) return [];
        if (n < 50) return ["<50"];
        if (n <= 200) return ["50–200"];
        if (n <= 500) return ["200–500"];
        if (n <= 1000) return ["500–1000"];
        return ["1000+"];
      } },
    ],
  },
  // ── CMS Profile groups (Iter 139.5 — ioannis ask 2026-05-26) ─────────
  // Pull the participation / system / financial signals from the
  // Lead Focus "CMS Profile" tab into the segment builder so reps can
  // build audiences off Ampliz/CMS facts (e.g. "ACO participants in
  // Pulmonology with allowed amount > $500K"). Each field uses a
  // valueGetter that maps the nested source shape (signals.*,
  // medicare.*, ehr.*) into the option vocab so the eval works
  // straight off the real CMS data without requiring rep tagging.
  {
    id: "cms_participation",
    label: "CMS Profile · Participation",
    fields: [
      { key: "aco_participation",           label: "ACO Participation",       options: TRI_STATE_OPTIONS, multi: true, forceDerive: true, valueGetter: (l) => _triBool(l?.aco_participation) },
      { key: "signals.medicare_assignment", label: "Medicare Assignment",     options: TRI_STATE_OPTIONS, multi: true, forceDerive: true, valueGetter: (l) => _triBool(l?.signals?.medicare_assignment) },
      { key: "signals.telehealth",          label: "Accepts TeleHealth",      options: TRI_STATE_OPTIONS, multi: true, forceDerive: true, valueGetter: (l) => _triBool(l?.signals?.telehealth) },
      { key: "signals.erx",                 label: "eRx (e-Prescribing)",     options: TRI_STATE_OPTIONS, multi: true, forceDerive: true, valueGetter: (l) => _triBool(l?.signals?.erx) },
      { key: "signals.pqrs",                label: "PQRS",                    options: TRI_STATE_OPTIONS, multi: true, forceDerive: true, valueGetter: (l) => _triBool(l?.signals?.pqrs) },
    ],
  },
  {
    id: "cms_system",
    label: "CMS Profile · System & Identifiers",
    fields: [
      { key: "ehr.vendor",                label: "EHR Vendor (CMS)",         options: ["Epic","Cerner","Athenahealth","NextGen","Allscripts","eClinicalWorks","Practice Fusion","Greenway","AdvancedMD","Kareo","DrChrono","Other"], multi: true, forceDerive: true, valueGetter: (l) => _coerceArr(l?.ehr?.vendor) },
      { key: "ehr.provider_stage_number", label: "Meaningful-Use Stage",     options: ["Stage 1","Stage 2","Stage 3"], multi: true, forceDerive: true, valueGetter: (l) => { const s = l?.ehr?.provider_stage_number; return s ? [`Stage ${s}`] : []; } },
      { key: "ehr.setting",               label: "EHR Setting",              options: ["Ambulatory","Inpatient","Hospital","Outpatient","Mixed"], multi: true, forceDerive: true, valueGetter: (l) => _coerceArr(l?.ehr?.setting) },
      { key: "insurance_type",            label: "Insurance Type",           options: ["Medicare","Medicaid","Commercial","Cash-Pay","Mixed","Other"], multi: true, forceDerive: true, valueGetter: (l) => _coerceArr(l?.insurance_type) },
      { key: "billing_status",            label: "Billing Status",           options: ["In-House","Outsourced","Hybrid","Transitioning","Unknown"], multi: true, forceDerive: true, valueGetter: (l) => _coerceArr(l?.billing_status) },
    ],
  },
  {
    id: "cms_financial",
    label: "CMS Profile · Financial Buckets",
    fields: [
      { key: "medicare.submitted", label: "Total Submitted Charges", options: FIN_BUCKETS, multi: true, forceDerive: true, valueGetter: (l) => { const b = _amount(l?.medicare?.submitted); return b ? [b] : []; } },
      { key: "medicare.allowed",   label: "Medicare Allowed Amount", options: FIN_BUCKETS, multi: true, forceDerive: true, valueGetter: (l) => { const b = _amount(l?.medicare?.allowed); return b ? [b] : []; } },
      { key: "medicare.payment",   label: "Medicare Payment",        options: FIN_BUCKETS, multi: true, forceDerive: true, valueGetter: (l) => { const b = _amount(l?.medicare?.payment); return b ? [b] : []; } },
    ],
  },
  {
    id: "cms_demographics",
    label: "CMS Profile · Beneficiary Demographics",
    fields: [
      { key: "demographics.age_skew",    label: "Age Skew",    options: ["Mostly <65","Mostly 65–74","Mostly 75–84","Mostly 85+","Mixed"], multi: true, forceDerive: true, valueGetter: (l) => {
        const a = l?.demographics?.age_bands || {};
        const buckets = { "Mostly <65": a.lt_65 || 0, "Mostly 65–74": a["65_74"] || 0, "Mostly 75–84": a["75_84"] || 0, "Mostly 85+": a.gt_84 || 0 };
        const total = Object.values(buckets).reduce((x, y) => x + y, 0);
        if (!total) return [];
        const top = Object.entries(buckets).sort((a, b) => b[1] - a[1])[0];
        return top[1] / total > 0.6 ? [top[0]] : ["Mixed"];
      } },
      { key: "demographics.gender_skew", label: "Gender Skew", options: ["Mostly Female","Mostly Male","Balanced"], multi: true, forceDerive: true, valueGetter: (l) => {
        const g = l?.demographics?.gender || {}; const f = g.female || 0, m = g.male || 0;
        if (!f && !m) return [];
        if (f > m * 1.3) return ["Mostly Female"];
        if (m > f * 1.3) return ["Mostly Male"];
        return ["Balanced"];
      } },
    ],
  },
];

/** Flat lookup: key → field meta. Useful for fast field-aware evaluation. */
export const EXTENDED_FILTER_FIELD_MAP = (() => {
  const m = {};
  for (const g of EXTENDED_FILTER_GROUPS) {
    for (const f of g.fields) {
      m[f.key] = f;
    }
  }
  return m;
})();

/** Pure evaluator for extended-field conditions. Used by LeadSegments. */
export function evalExtendedCondition(lead, field, value) {
  if (!Array.isArray(value) || !value.length) return true;
  const meta = EXTENDED_FILTER_FIELD_MAP[field];
  // Iter 139.5 (ioannis 2026-05-26): three behaviors for sourcing the
  // lead's value, depending on the field type:
  //   1) `forceDerive` fields (CMS tri-states, nested EHR/medicare
  //      paths) ALWAYS run the valueGetter so the boolean / nested
  //      shape gets normalised to the option vocab ("Yes"/"No"/"Unknown",
  //      "$500K – $1M", etc).
  //   2) Fields with a `valueGetter` and a stored value → prefer the
  //      stored value (rep tag wins, e.g. providers_multi).
  //   3) Fields with `valueGetter` and NO stored value → derive from
  //      source data (group_size → MD buckets, contacts → specialty).
  let leadValue;
  if (meta?.valueGetter && typeof meta.valueGetter === "function") {
    if (meta.forceDerive) {
      leadValue = meta.valueGetter(lead);
    } else {
      const stored = lead?.[field];
      const hasStored = Array.isArray(stored) ? stored.length > 0 : (stored != null && stored !== "");
      leadValue = hasStored ? stored : meta.valueGetter(lead);
    }
  } else {
    leadValue = lead?.[field];
  }
  if (!meta) {
    if (Array.isArray(leadValue)) return leadValue.some((v) => value.includes(v));
    return value.includes(leadValue);
  }
  if (meta.multi) {
    const arr = Array.isArray(leadValue) ? leadValue : (leadValue != null && leadValue !== "" ? [leadValue] : []);
    return arr.some((v) => value.includes(v));
  }
  return value.includes(leadValue);
}
